import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ArrowUpRight,
  ChevronDown,
  CircleAlert,
  CircleCheck,
  TriangleAlert,
} from "lucide-react";
import type { ReactNode } from "react";
import { LtrValue } from "../../components/common/LtrValue.js";
import { cn } from "../../lib/utils.js";
import type { CampaignDraftStepId } from "./campaign-draft-workflow.js";
import {
  summarizeCampaignReviewContent,
  type CampaignReviewChecklistRow,
  type CampaignReviewChecklistRowId,
  type CampaignReviewChecklistRowState,
} from "./campaign-review-presentation.js";
import { CampaignReviewGroup } from "./CampaignReviewGroup.js";
import { CampaignTemplateMetaChips } from "./CampaignTemplateCard.js";

type ContentItemId = Exclude<CampaignReviewChecklistRowId, "audience">;

function contentItemTitleKey(id: ContentItemId): string {
  switch (id) {
    case "sender":
      return "campaigns.review.checklist.sender";
    case "template":
      return "campaigns.review.checklist.template";
    case "personalization":
      return "campaigns.review.checklist.personalization";
    default: {
      const _exhaustive: never = id;
      return _exhaustive;
    }
  }
}

function StateIcon({
  state,
  size = 16,
}: {
  state: CampaignReviewChecklistRowState;
  size?: number;
}): React.JSX.Element {
  switch (state) {
    case "ready":
      return <CircleCheck size={size} className="shrink-0 text-success" aria-hidden="true" />;
    case "warning":
      return <TriangleAlert size={size} className="shrink-0 text-warning" aria-hidden="true" />;
    case "blocked":
      return <CircleAlert size={size} className="shrink-0 text-error" aria-hidden="true" />;
    default: {
      const _exhaustive: never = state;
      return _exhaustive;
    }
  }
}

const fixButtonClass =
  "inline-flex shrink-0 items-center gap-1 rounded-md px-1.5 py-1 text-[0.75rem] font-semibold text-foreground underline underline-offset-[0.2em] hover:bg-surface-muted focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--color-ring)]";

function FixButton({
  label,
  testId,
  onClick,
}: {
  label: string;
  testId: string;
  onClick: () => void;
}): React.JSX.Element {
  return (
    <button type="button" className={fixButtonClass} onClick={onClick} data-testid={testId}>
      {label}
      <ArrowUpRight size={13} className="rtl:-scale-x-100" aria-hidden="true" />
    </button>
  );
}

export interface CampaignReviewChecklistDetails {
  executableCount: number;
  excludedCount: number;
  senderName: string;
  senderPhone: string | null;
  templateName: string;
  templateLanguage: string | null;
  templateCategory: string | null;
  mappedVariableCount: number;
}

export interface CampaignReviewDeliverySummary {
  mode: "now" | "schedule";
  /** Formatted local date and time; null until a schedule is chosen. */
  schedule: { date: string; time: string } | null;
  timezone: string;
  onChooseSchedule?: () => void;
}

export interface CampaignReviewCreditsSummary {
  isUnlimited: boolean;
  requiredCredits?: number;
  availableCredits?: string;
  reservedCredits?: string;
  insufficientCredits: boolean;
}

export function CampaignReviewChecklist({
  rows,
  details,
  messageUnlocked,
  delivery,
  credits,
  onFix,
}: {
  rows: readonly CampaignReviewChecklistRow[];
  details: CampaignReviewChecklistDetails;
  messageUnlocked: boolean;
  delivery?: CampaignReviewDeliverySummary;
  credits?: CampaignReviewCreditsSummary;
  onFix?: (step: CampaignDraftStepId) => void;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const [contentExpanded, setContentExpanded] = useState(false);
  const contentPanelId = useId();
  const formatCount = (value: number) => new Intl.NumberFormat(i18n.language).format(value);
  const audience = rows.find((row) => row.id === "audience");
  const content = summarizeCampaignReviewContent(rows, messageUnlocked);
  const canExpandContent = !content.locked && content.items.length > 0;

  const reason = (row: CampaignReviewChecklistRow): string | null =>
    row.reasonKey ? t(row.reasonKey, { count: formatCount(row.missingRecipients) }) : null;

  const contentItemReady = (id: ContentItemId): ReactNode => {
    switch (id) {
      case "sender":
        return (
          <>
            <span>{details.senderName}</span>
            {details.senderPhone ? (
              <>
                <span aria-hidden="true"> · </span>
                <LtrValue>{details.senderPhone}</LtrValue>
              </>
            ) : null}
          </>
        );
      case "template":
        return (
          <span className="inline-flex min-w-0 flex-wrap items-center gap-1.5">
            <span className="truncate">{details.templateName}</span>
            <CampaignTemplateMetaChips
              language={details.templateLanguage}
              category={details.templateCategory}
              status={null}
            />
          </span>
        );
      case "personalization":
        return t("campaigns.review.checklist.personalizationReady", {
          count: formatCount(details.mappedVariableCount),
        });
      default: {
        const _exhaustive: never = id;
        return _exhaustive;
      }
    }
  };

  const contentDescription = content.locked
    ? t("campaigns.review.checklist.contentLocked")
    : content.state === "ready"
      ? t("campaigns.review.checklist.contentReady")
      : t("campaigns.review.checklist.contentAttention", { count: content.attentionCount });

  const contentHeading = (
    <>
      <span className="mt-0.5">
        <StateIcon state={content.state} />
      </span>
      <span className="grid min-w-0 flex-1 gap-0.5 text-start">
        <span className="text-[0.8125rem] font-medium text-foreground">
          {t("campaigns.review.checklist.content")}
        </span>
        <span className="text-[0.75rem] leading-snug text-muted">{contentDescription}</span>
      </span>
    </>
  );

  return (
    <CampaignReviewGroup
      titleId="campaign-review-checklist-title"
      title={t("campaigns.review.checklist.title")}
    >
      <ul
        className="m-0 grid list-none divide-y divide-line/60 rounded-lg border border-line/70 bg-surface p-0"
        data-testid="campaign-review-checklist"
      >
        {audience ? (
          <li
            className="flex min-w-0 items-start gap-3 px-3 py-2.5"
            data-testid="campaign-review-check-audience"
            data-state={audience.state}
          >
            <span className="mt-0.5">
              <StateIcon state={audience.state} />
            </span>
            <div className="grid min-w-0 flex-1 gap-0.5">
              <p className="m-0 text-[0.8125rem] font-medium">
                {t("campaigns.review.checklist.audience")}
              </p>
              <p className="m-0 min-w-0 text-[0.75rem] leading-snug text-muted">
                {reason(audience) ?? (
                  <>
                    <span data-testid="campaign-launch-ready-count">
                      {t("campaigns.review.checklist.audienceReady", {
                        count: formatCount(details.executableCount),
                      })}
                    </span>
                    {details.excludedCount > 0 ? (
                      <>
                        <span aria-hidden="true"> · </span>
                        <span data-testid="campaign-launch-excluded-count">
                          {t("campaigns.review.checklist.audienceExcluded", {
                            count: formatCount(details.excludedCount),
                          })}
                        </span>
                      </>
                    ) : null}
                  </>
                )}
              </p>
            </div>
            {audience.fixStep && onFix ? (
              <FixButton
                label={t("campaigns.review.checklist.fix")}
                testId="campaign-review-fix-audience"
                onClick={() => onFix(audience.fixStep ?? "audience")}
              />
            ) : null}
          </li>
        ) : null}

        <li
          className="grid min-w-0"
          data-testid="campaign-review-check-content"
          data-state={content.state}
        >
          {canExpandContent ? (
            <button
              type="button"
              className="flex min-w-0 items-start gap-3 px-3 py-2.5 text-start transition-colors hover:bg-surface-muted/50 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-inset focus-visible:ring-[var(--color-ring)]"
              aria-expanded={contentExpanded}
              aria-controls={contentPanelId}
              onClick={() => setContentExpanded((current) => !current)}
              data-testid="campaign-review-content-toggle"
            >
              {contentHeading}
              <span className="mt-0.5 inline-flex shrink-0 items-center gap-1 text-[0.75rem] font-medium text-muted">
                {contentExpanded
                  ? t("campaigns.review.checklist.hideDetails")
                  : t("campaigns.review.checklist.showDetails")}
                <ChevronDown
                  size={14}
                  className={cn("transition-transform", contentExpanded ? "rotate-180" : null)}
                  aria-hidden="true"
                />
              </span>
            </button>
          ) : (
            <div className="flex min-w-0 items-start gap-3 px-3 py-2.5">{contentHeading}</div>
          )}
          {canExpandContent && contentExpanded ? (
            <ul
              id={contentPanelId}
              className="m-0 mb-2.5 ms-[2.375rem] me-3 grid list-none gap-0 border-s border-line/70 p-0"
              data-testid="campaign-review-content-items"
            >
              {content.items.map((item) => {
                const id = item.id as ContentItemId;
                const fixStep = item.fixStep;
                return (
                  <li
                    key={item.id}
                    className="flex min-w-0 items-start gap-2.5 py-1.5 ps-3"
                    data-testid={`campaign-review-check-${item.id}`}
                    data-state={item.state}
                  >
                    <span className="mt-px">
                      <StateIcon state={item.state} size={14} />
                    </span>
                    <div className="grid min-w-0 flex-1 gap-0.5">
                      <p className="m-0 text-[0.75rem] font-medium">{t(contentItemTitleKey(id))}</p>
                      <p className="m-0 min-w-0 text-[0.75rem] leading-snug text-muted">
                        {reason(item) ?? contentItemReady(id)}
                      </p>
                    </div>
                    {fixStep && onFix ? (
                      <FixButton
                        label={t("campaigns.review.checklist.fix")}
                        testId={`campaign-review-fix-${item.id}`}
                        onClick={() => onFix(fixStep)}
                      />
                    ) : null}
                  </li>
                );
              })}
            </ul>
          ) : null}
        </li>

        {delivery ? (
          <li
            className="flex min-w-0 items-start gap-3 px-3 py-2.5"
            data-testid="campaign-review-check-delivery"
            data-state={delivery.mode === "schedule" && !delivery.schedule ? "warning" : "ready"}
          >
            <span className="mt-0.5">
              <StateIcon
                state={delivery.mode === "schedule" && !delivery.schedule ? "warning" : "ready"}
              />
            </span>
            <div className="grid min-w-0 flex-1 gap-0.5">
              <p className="m-0 text-[0.8125rem] font-medium">
                {t("campaigns.review.checklist.delivery")}
              </p>
              {delivery.mode === "now" ? (
                <p className="m-0 text-[0.75rem] leading-snug text-muted">
                  {t("campaigns.review.launch.sendNow")}
                </p>
              ) : delivery.schedule ? (
                <div className="grid gap-0.5" data-testid="campaign-schedule-summary">
                  <p className="m-0 text-[0.75rem] font-medium leading-snug text-foreground">
                    {delivery.schedule.date}
                    <span className="text-muted" aria-hidden="true">
                      {" · "}
                    </span>
                    <LtrValue className="tabular-nums">{delivery.schedule.time}</LtrValue>
                  </p>
                  <p className="m-0 text-[0.75rem] leading-snug text-muted">
                    <LtrValue>{delivery.timezone}</LtrValue>
                  </p>
                </div>
              ) : (
                <p className="m-0 text-[0.75rem] leading-snug text-muted">
                  {t("campaigns.review.checklist.deliveryChoose")}
                </p>
              )}
            </div>
            {delivery.mode === "schedule" && delivery.onChooseSchedule ? (
              <FixButton
                label={
                  delivery.schedule
                    ? t("campaigns.message.change")
                    : t("campaigns.review.checklist.fix")
                }
                testId="campaign-review-fix-delivery"
                onClick={delivery.onChooseSchedule}
              />
            ) : null}
          </li>
        ) : null}

        {credits ? (
          <li
            className="flex min-w-0 items-start gap-3 px-3 py-2.5"
            data-testid="campaign-review-check-credits"
            data-state={credits.insufficientCredits ? "blocked" : "ready"}
          >
            <span className="mt-0.5">
              <StateIcon state={credits.insufficientCredits ? "blocked" : "ready"} />
            </span>
            <div className="grid min-w-0 flex-1 gap-0.5">
              <p className="m-0 text-[0.8125rem] font-medium">
                {t("campaigns.review.checklist.credits")}
              </p>
              {credits.isUnlimited ? (
                credits.availableCredits !== undefined ? (
                  <p className="m-0 text-[0.75rem] leading-snug text-muted">
                    {t("campaigns.review.credits.unlimitedSummary", {
                      count: credits.requiredCredits,
                    })}
                  </p>
                ) : (
                  <p className="m-0 text-[0.75rem] leading-snug text-muted">
                    {t("campaigns.review.credits.unlimited")}
                  </p>
                )
              ) : credits.availableCredits !== undefined ? (
                credits.insufficientCredits ? (
                  <p className="m-0 text-[0.75rem] leading-snug text-error font-medium" role="alert">
                    {t("campaigns.review.credits.insufficientAvailable", {
                      count: credits.requiredCredits,
                      required: credits.requiredCredits,
                      available: credits.availableCredits,
                    })}
                  </p>
                ) : (
                  <p className="m-0 text-[0.75rem] leading-snug text-muted">
                    {t("campaigns.review.credits.sufficientAvailable", {
                      count: credits.requiredCredits,
                      required: credits.requiredCredits,
                      available: credits.availableCredits,
                    })}
                    {credits.reservedCredits && BigInt(credits.reservedCredits) > 0n ? (
                      <>
                        <span aria-hidden="true"> · </span>
                        <span>{t("campaigns.review.credits.reservedCount", { count: Number(credits.reservedCredits) })}</span>
                      </>
                    ) : null}
                  </p>
                )
              ) : credits.insufficientCredits ? (
                <p className="m-0 text-[0.75rem] leading-snug text-error font-medium" role="alert">
                  {t("campaigns.review.credits.insufficientAgent")}
                </p>
              ) : (
                <p className="m-0 text-[0.75rem] leading-snug text-muted">
                  {t("campaigns.review.credits.sufficientAgent")}
                </p>
              )}
            </div>
          </li>
        ) : null}
      </ul>
    </CampaignReviewGroup>
  );
}
